// src/components/bot/InternalBotV2.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Layer 1 — Chat UI Component (minimal changes from original)
//
// What changed from the original:
//   REMOVED — fetchMenu useEffect (/chat-boat menu loading)
//   REMOVED — handleOptionClick, showMainMenu, findAllOptionsByName (menu logic)
//   REMOVED — rootMenuRef, initialFetchDone, msgsRef (menu state)
//   REMOVED — fmt, bullets, formatGenericResponse (formatting utilities)
//   REMOVED — botFetch import
//   REMOVED — msg.options / msg.onOptionClick rendering in Bubble (menu buttons)
//   REMOVED — "Reset to Main Menu" footer button
//   ADDED   — import { useChatController }
//   ADDED   — const { messages, loading, send } = useChatController()
//   UPDATED — subtitle: "AI-powered · Live data"
//   UPDATED — loading indicator text: "Thinking…"
//   UPDATED — input placeholder: "Ask about any app data…"
//
// Everything else — all style={{...}} props, panel layout, scroll, header,
// floating button, input row — is pixel-identical to the original.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useRef, useEffect } from 'react';
import { useChatController } from './useChatController';

const SUGGESTIONS = [
  'Dashboard KPIs',
  'Show low stock drugs',
  'Attendance today',
  'Pending indents',
  'Cases treated',
  'AI services report',
  'List employees in Karimnagar',
  'IoT sensor alerts',
];

// ── Bubble — chat message bubble ──────────────────────────────────────────────
// Renders normal text, and if msg.options exists, renders a list of clickable buttons.
function Bubble({ msg, onOptionClick }) {
  const isBot = msg.role === 'bot';
  return (
    <div style={{ display: 'flex', justifyContent: isBot ? 'flex-start' : 'flex-end', marginBottom: 8 }}>
      {isBot && (
        <div style={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, marginRight: 7, marginTop: 1,
        }}>🤖</div>
      )}
      <div style={{
        maxWidth: '85%', padding: '6px 10px',
        borderRadius: isBot ? '4px 14px 14px 14px' : '14px 4px 14px 14px',
        background: isBot ? '#f1f5f9' : 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
        color: isBot ? '#1e293b' : '#fff',
        fontSize: 11, lineHeight: 1.5,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
      }}>
        {msg.text}
        {msg.loading && <span style={{ opacity: 0.5, animation: 'blink 1s step-start infinite' }}>▋</span>}
        
        {/* Render clickable options if provided */}
        {msg.options && msg.options.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {msg.options.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => onOptionClick && onOptionClick(opt)}
                style={{
                  background: '#fff', border: '1px solid #3b82f6',
                  color: '#3b82f6', padding: '6px 12px', borderRadius: 20,
                  fontSize: 11, cursor: 'pointer', textAlign: 'center',
                  fontFamily: 'inherit', transition: 'background 0.2s',
                }}
                onMouseOver={e => e.target.style.background = '#eff6ff'}
                onMouseOut={e => e.target.style.background = '#fff'}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── InternalBotV2 — main component ───────────────────────────────────────────
export default function InternalBotV2() {
  const [open,  setOpen]  = useState(false);
  const [input, setInput] = useState('');

  const bottomRef = useRef();
  const inputRef  = useRef();

  // All orchestration is delegated to the Chat Controller hook.
  // This component is now a pure view layer.
  const { messages, loading, send, clearChat } = useChatController();

  // Scroll to latest message when the panel is open or messages change
  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [open, messages.length]);

  const handleSend = () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    send(q);
  };

  const onKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const hasUserMsg = messages.some(m => m.role === 'user');

  return (
    <>
      {/* ── Floating toggle button ── */}
      <button
        onClick={() => setOpen(o => !o)}
        title="VAHD Info Bot"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9000,
          width: 52, height: 52, borderRadius: '50%', border: 'none',
          background: open ? '#1e3a8a' : 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
          color: '#fff', fontSize: open ? 18 : 22, cursor: 'pointer',
          boxShadow: '0 4px 22px rgba(29,78,216,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform .2s, background .2s',
          transform: open ? 'rotate(45deg)' : 'none',
        }}
        aria-label="Toggle VAHD Info Bot"
      >
        {open ? '✕' : '🤖'}
      </button>

      {/* ── Chat panel ── */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 86, right: 24, zIndex: 8999,
          width: 380, maxWidth: 'calc(100vw - 32px)',
          height: 520, maxHeight: 'calc(100vh - 130px)',
          background: '#fff',
          border: '1px solid rgba(226,232,240,0.9)',
          borderRadius: 20,
          boxShadow: '0 24px 64px rgba(15,23,42,0.2), 0 6px 20px rgba(29,78,216,0.12)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'dropIn .18s ease',
        }}>

          {/* ── Header ── */}
          <div style={{
            padding: '12px 16px', flexShrink: 0,
            background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
            }}>🤖</div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>VAHD Info Bot</div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10 }}>AI-powered · Live data</div>
            </div>
            
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button
                onClick={clearChat}
                title="Restart Chat"
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 16 }}
              >↻</button>
              <button
                onClick={() => setOpen(false)}
                title="Close Chat"
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 16 }}
              >✕</button>
            </div>
          </div>

          {/* ── Message list ── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 4px', minHeight: 0 }}>
            {messages.map(m => <Bubble key={m.id} msg={m} onOptionClick={handleSend} />)}
            {loading && <Bubble msg={{ id: 'thinking', role: 'bot', text: 'Thinking…', loading: true }} />}
            <div ref={bottomRef} style={{ height: 10 }} />
          </div>

          {/* ── Suggestions ── */}
          {!hasUserMsg && !loading && (
            <div style={{ padding: '4px 12px 8px', flexShrink: 0, borderTop: '1px solid var(--bdr)' }}>
              <div style={{ fontSize: 10, color: 'var(--txt3)', fontWeight: 600, marginBottom: 5 }}>Quick queries:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => send(s)} style={{
                    padding: '4px 9px', borderRadius: 20,
                    border: '1px solid #bfdbfe', background: '#eff6ff',
                    color: '#1d4ed8', fontSize: 10.5, cursor: 'pointer',
                    fontFamily: 'inherit', lineHeight: 1.4,
                    transition: 'background .12s',
                  }}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {/* ── Input row ── */}
          <div style={{ padding: '8px 10px', borderTop: '1px solid var(--bdr)', display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about any app data…"
              disabled={loading}
              style={{
                flex: 1, padding: '7px 12px', borderRadius: 20,
                border: '1.5px solid var(--bdr)', fontSize: 12,
                fontFamily: 'inherit', outline: 'none', background: '#fff',
                transition: 'border .15s',
              }}
              onFocus={e => { e.target.style.borderColor = '#3b82f6'; }}
              onBlur={e  => { e.target.style.borderColor = 'var(--bdr)'; }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              style={{
                width: 36, height: 36, borderRadius: '50%', border: 'none', flexShrink: 0,
                background: input.trim() && !loading ? 'linear-gradient(135deg,#1d4ed8,#3b82f6)' : '#e2e8f0',
                color: input.trim() && !loading ? '#fff' : '#94a3b8',
                fontSize: 15, cursor: input.trim() && !loading ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background .15s',
              }}
              aria-label="Send"
            >➤</button>
          </div>

        </div>
      )}
    </>
  );
}
